import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className = '', width, height }) => {
  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={`animate-pulse bg-slate-700/50 rounded ${className}`}
      style={style}
    />
  );
};

export const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({
  lines = 1,
  className = '',
}) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} className="h-4" width={i === lines - 1 ? '75%' : '100%'} />
    ))}
  </div>
);

export const MarketPulseSkeleton: React.FC = () => (
  <div className="grid grid-cols-6 gap-4">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="text-center">
        <Skeleton className="h-3 w-16 mx-auto mb-2" />
        <Skeleton className="h-5 w-20 mx-auto mb-1" />
        <Skeleton className="h-4 w-12 mx-auto mb-2" />
        <Skeleton className="h-6 w-full" />
      </div>
    ))}
  </div>
);

export const HotMoversSkeleton: React.FC = () => (
  <div className="grid grid-cols-2 gap-6">
    <div>
      <Skeleton className="h-4 w-24 mb-3" />
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between py-2 border-b border-slate-700/50">
            <div className="flex items-center gap-2">
              <Skeleton className="w-4 h-4 rounded" />
              <div>
                <Skeleton className="h-4 w-16 mb-1" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <Skeleton className="h-5 w-14" />
          </div>
        ))}
      </div>
    </div>
    <div>
      <Skeleton className="h-4 w-24 mb-3" />
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between py-2 border-b border-slate-700/50">
            <div className="flex items-center gap-2">
              <Skeleton className="w-4 h-4 rounded" />
              <div>
                <Skeleton className="h-4 w-16 mb-1" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <Skeleton className="h-5 w-14" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const WatchlistSkeleton: React.FC = () => (
  <div className="space-y-0">
    <div className="grid grid-cols-7 gap-4 pb-3 text-xs text-slate-500">
      <div>Symbol</div>
      <div>Name</div>
      <div>Exchange</div>
      <div className="text-right">Price</div>
      <div className="text-right">Change</div>
      <div className="text-center">Alerts</div>
      <div></div>
    </div>
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="grid grid-cols-7 gap-4 py-3 border-t border-slate-700/50">
        <Skeleton className="h-5 w-14" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-5 w-12" />
        <Skeleton className="h-5 w-20 ml-auto" />
        <Skeleton className="h-5 w-14 ml-auto" />
        <Skeleton className="h-5 w-10 mx-auto" />
        <Skeleton className="h-5 w-5 ml-auto" />
      </div>
    ))}
  </div>
);

export const CardSkeleton: React.FC<{ height?: string }> = ({ height = 'h-48' }) => (
  <div className={`bg-slate-800/50 rounded-xl border border-slate-700/50 p-6 ${height}`}>
    <div className="flex justify-between items-center mb-4">
      <Skeleton className="h-6 w-32" />
      <Skeleton className="h-4 w-20" />
    </div>
    <SkeletonText lines={3} />
  </div>
);

export const SignalsSkeleton: React.FC = () => (
  <div className="space-y-4">
    {/* Summary bar */}
    <div className="space-y-3">
      <Skeleton className="h-2 w-full rounded-full" />
      <div className="flex justify-between">
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-5 w-16" />
      </div>
    </div>
    {/* Signal list */}
    <div className="space-y-3 pt-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center justify-between py-2 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <Skeleton className="w-8 h-8 rounded" />
            <div>
              <Skeleton className="h-4 w-20 mb-1" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
          <Skeleton className="h-6 w-20 rounded-lg" />
        </div>
      ))}
    </div>
  </div>
);

export default Skeleton;
